import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import ContactDialog from "../../dashboard/contacts/dialog";
import { ContactsSelector } from "../moduleSelectors/contactSelector";
import { PMEntityPicker } from "./PMEntityPicker";
import { InitialsAvatar, PICKER_PAGE_SIZE, joinParts, rowsFrom } from "./pickerParts";

export const contactToItem = (row) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: row.displayTitle ?? row.name ?? `#${row.id}`,
        secondary: joinParts(row.email || row.mobile, row.customer?.displayTitle ?? row.customer?.name),
        leading: <InitialsAvatar text={row.displayTitle ?? row.name} />,
        object: row,
      };

export async function fetchContactOptions({ query, filter, customerId }) {
  const byCustomer = filter === "customer" && customerId != null;
  const response = await axios.get("/contacts/", {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      sortBy: "created_at",
      sort: "desc",
      filters: byCustomer ? [{ name: "CUSTOMER", value: customerId }] : undefined,
    },
  });
  return rowsFrom(response).map(contactToItem);
}

/**
 * Contact picker (new design). Defaults to the selected customer's contacts
 * with a one-tap switch to all. Emits the legacy ContactsSelector contract:
 * `onChange({ target: { name, value, data } })`. "New contact" reuses the
 * legacy create form, pre-linked to the customer.
 */
export function ContactPicker({
  name = "contact_id",
  label,
  value,
  customer,
  onChange,
  setFieldValue,
  disabled = false,
  error,
  required = false,
  allowCreate = true,
}) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const selectedId = value?.id ?? null;
  const customerId = customer?.id ?? null;

  const filters = useMemo(
    () => [
      ...(customerId != null ? [{ key: "customer", label: t("pickers.contact.filterCustomer") }] : []),
      { key: "all", label: t("pickers.contact.filterAll") },
    ],
    [customerId, t]
  );

  const loadOptions = useCallback(
    ({ query, filter }) => fetchContactOptions({ query, filter, customerId }),
    [customerId]
  );

  const emit = (row) => {
    const id = row?.id ?? null;
    setFieldValue?.(name, id);
    onChange?.({ target: { name, value: id, data: row ?? null } });
  };

  return (
    <>
      <PMEntityPicker
        label={label ?? t("Contact")}
        labelAction={selectedId != null ? { action: () => setDetailOpen(true) } : null}
        required={required}
        value={contactToItem(value)}
        placeholder={t("pickers.contact.placeholder")}
        searchPlaceholder={t("pickers.contact.search")}
        disabled={disabled}
        error={error}
        loadOptions={loadOptions}
        filters={filters}
        defaultFilter={customerId != null ? "customer" : "all"}
        pageSize={PICKER_PAGE_SIZE}
        onSelect={(item) => emit(item?.object ?? null)}
        emptyText={t("pickers.contact.empty")}
        footerAction={allowCreate ? { label: t("pickers.contact.create"), onClick: () => setCreateOpen(true) } : undefined}
      />
      {createOpen && (
        <ContactsSelector
          showField={false}
          open
          startInCreateView
          name={name}
          customer={customer}
          onClose={() => setCreateOpen(false)}
          onChange={(event) => {
            setCreateOpen(false);
            if (event?.target?.data?.id != null) emit(event.target.data);
          }}
        />
      )}
      {detailOpen && selectedId != null && (
        <ContactDialog
          open
          onClose={() => setDetailOpen(false)}
          readOnly
          selected={{ id: selectedId }}
        />
      )}
    </>
  );
}
